import { useState, type FC } from 'react'
import {
    Tabs,
    Stack,
    Group,
    Text,
    Badge,
    Button,
    Paper,
    Divider,
    Title,
    SimpleGrid,
    ActionIcon,
    Tooltip,
    Anchor,
} from '@mantine/core'
import {
    IconAdjustmentsHorizontal,
    IconChartDots3,
    IconSend,
    IconSettings,
    IconPlayerPause,
    IconPlayerPlay,
    IconChevronDown,
    IconChevronUp,
} from '@tabler/icons-react'
import { DeleteButton } from '@/components/DeleteButton'
import { AttentionBanner } from '@/components/AttentionBanner'
import { formatDistanceToNow } from 'date-fns'
import type { AudienceConfiguration, Dataset } from '@/lib/domain'
import { formatCompactNumber } from '@/lib/formatNumber'
import { cpmFor, formatCpm } from '@/lib/pricing'
import {
    defaultDestinationRoute,
    findDestination,
    MARKETPLACE_DELIVERY_LABEL,
} from '@/lib/marketplaceDestinations'
import { segmentKey } from '../api'
import { useSegmentStubs } from '../queries'
import type { Distribution, DistributionRun } from '../types'
import {
    MODELING_PRIORITY_LABEL,
    RUN_STATUS_META,
    SCHEDULE_LABEL,
} from '../labels'
import { SegmentMakeup } from './SegmentMakeup'
import { AudienceInsights } from './AudienceInsights'
import { ModelingOptions, type ModelConfig } from './ModelingOptions'
import { DistributeModal } from './DistributeModal'
import { DistributionConfigModal } from './DistributionConfigModal'

export type SegmentStage = 'makeup' | 'model' | 'distribute'

// Records + next step + time for one run (or "Not run yet" when absent).
const RunSummary: FC<{ run?: DistributionRun }> = ({ run }) => {
    if (!run) {
        return (
            <Text size='xs' c='dimmed'>
                Not run yet
            </Text>
        )
    }
    const delivered = run.recordsMatched === null && run.recordsPushed != null
    const matched = run.recordsMatched != null && run.recordsPushed
    return (
        <Stack gap={0}>
            {delivered && (
                <Text size='xs' c='dimmed'>
                    Delivered {formatCompactNumber(run.recordsPushed ?? 0)} rows
                </Text>
            )}
            {matched && (
                <Text size='xs' c='dimmed'>
                    {formatCompactNumber(run.recordsMatched ?? 0)} matched of{' '}
                    {formatCompactNumber(run.recordsPushed ?? 0)} (
                    {Math.round(
                        ((run.recordsMatched ?? 0) / (run.recordsPushed || 1)) *
                            100
                    )}
                    %)
                </Text>
            )}
            {run.nextStep && (
                <Text size='xs' c='warning'>
                    {run.nextStep}
                </Text>
            )}
            {run.runAt && (
                <Text size='xs' c='dimmed'>
                    {formatDistanceToNow(new Date(run.runAt), {
                        addSuffix: true,
                    })}
                </Text>
            )}
        </Stack>
    )
}

// How a distribution is delivered, shown generically: "Direct" or "Marketplace".
// The specific data marketplace behind an indirect route isn't surfaced to the
// buyer. Falls back to the destination's default route when none is saved yet.
const routeLabel = (
    distribution: Distribution,
    platform: string
): string | null => {
    const dest = findDestination(platform)
    const route =
        distribution.route ?? (dest ? defaultDestinationRoute(dest) : undefined)
    if (!route) return null
    return route.kind === 'direct'
        ? 'Direct'
        : MARKETPLACE_DELIVERY_LABEL[route.via]
}

const Stat: FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <Paper withBorder p='md' radius='md'>
        <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
            {label}
        </Text>
        <Group gap='xs' mt={4} align='baseline'>
            {children}
        </Group>
    </Paper>
)

export interface SegmentDetailProps {
    config: AudienceConfiguration
    threadId: string
    // Dataset id → display name, for the prominent data-source label.
    names: Record<string, string>
    // The thread's available datasets; the user-attribute ones are offered as
    // expansion (lookalike) modeling targets.
    datasets: Dataset[]
    initialStage?: SegmentStage
    onClose: () => void
}

// Shared identity band shown above the tabs, so the audience's headline reach
// (hero), model-aware CPM, and marketer summary stay in view across Makeup /
// Model / Distribute.
const AudienceSummary: FC<{
    config: AudienceConfiguration
    isModeled: boolean
}> = ({ config, isModeled }) => {
    const reach =
        config.audience_size > 0
            ? `~${formatCompactNumber(config.audience_size)}`
            : '—'
    const cpm = cpmFor(config.dataset_type, isModeled)
    return (
        <Stack gap='sm' mb='md'>
            <Group
                justify='space-between'
                align='flex-end'
                wrap='nowrap'
                gap='md'
            >
                {cpm !== undefined ? (
                    <Group gap={6} align='baseline' wrap='nowrap'>
                        <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                            CPM
                        </Text>
                        <Text fz={20} fw={500} lh={1.1}>
                            {formatCpm(cpm)}
                        </Text>
                    </Group>
                ) : (
                    <div />
                )}
                <Group gap={6} align='baseline' wrap='nowrap'>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                        Estimated reach
                    </Text>
                    <Text
                        fz={32}
                        fw={700}
                        lh={1.1}
                        c='var(--mantine-primary-color-filled)'
                    >
                        {reach}
                    </Text>
                </Group>
            </Group>
            {config.description && <Text c='dimmed'>{config.description}</Text>}
        </Stack>
    )
}

export const SegmentDetail: FC<SegmentDetailProps> = ({
    config,
    threadId,
    names,
    datasets,
    initialStage = 'makeup',
    onClose,
}) => {
    const {
        lifecycleFor,
        destinations,
        saveModeling,
        setDistribution,
        removeDistribution,
    } = useSegmentStubs()
    const key = segmentKey(config)
    const life = lifecycleFor(key, threadId)

    const [remodel, setRemodel] = useState(false)
    const [distributeOpen, setDistributeOpen] = useState(false)
    // Which delivery's run history is expanded (by distribution id).
    const [openRuns, setOpenRuns] = useState<string | null>(null)
    // The distribution config being edited, if any.
    const [editing, setEditing] = useState<Distribution | null>(null)

    const seedSize = config.audience_size ?? 0

    const handleCreateModel = (model: ModelConfig) => {
        saveModeling({
            threadId,
            segmentKey: key,
            type: model.modelType === 'expansion' ? 'lookalike' : 'propensity',
            priority: model.selectedModel,
            similarityPct: model.lookalikePercentage,
            seedSize,
            expandedSize: model.expandedSize,
            expansionDatasetId: model.expansionDatasetId,
            excludeSeed: model.excludeSeed,
            status: 'ready',
        })
        setRemodel(false)
    }

    return (
        <Tabs defaultValue={initialStage} keepMounted={false}>
            <AudienceSummary
                config={config}
                isModeled={life.modelStatus === 'ready'}
            />
            <Tabs.List mb='md'>
                <Tabs.Tab
                    value='makeup'
                    leftSection={<IconAdjustmentsHorizontal size={16} />}
                >
                    Makeup
                </Tabs.Tab>
                <Tabs.Tab
                    value='model'
                    leftSection={<IconChartDots3 size={16} />}
                >
                    Model
                    {life.modelStatus === 'ready' && (
                        <Badge
                            ml='xs'
                            size='xs'
                            color='success'
                            variant='light'
                        >
                            Ready
                        </Badge>
                    )}
                </Tabs.Tab>
                <Tabs.Tab
                    value='distribute'
                    leftSection={<IconSend size={16} />}
                >
                    Distribute
                    {life.distributeStatus === 'attention' && (
                        <Badge
                            ml='xs'
                            size='xs'
                            color='warning'
                            variant='light'
                        >
                            Needs you
                        </Badge>
                    )}
                    {life.distributeStatus === 'live' && (
                        <Badge
                            ml='xs'
                            size='xs'
                            color='success'
                            variant='light'
                        >
                            Live
                        </Badge>
                    )}
                </Tabs.Tab>
            </Tabs.List>

            {/* --- Makeup --- */}
            <Tabs.Panel value='makeup'>
                <Stack gap='lg'>
                    <SegmentMakeup config={config} names={names} />

                    {/* AI insight charts for every segment type — transaction,
                        user-attribute, and crossed. The component picks the right
                        per-type LLM endpoint internally. */}
                    <AudienceInsights config={config} />
                </Stack>
            </Tabs.Panel>

            {/* --- Model --- */}
            <Tabs.Panel value='model'>
                {life.modeling?.status === 'ready' && !remodel ? (
                    <Stack gap='md'>
                        <Group justify='space-between'>
                            <Title order={5}>
                                {life.modeling.type === 'lookalike'
                                    ? 'Lookalike expansion'
                                    : 'Propensity model'}
                            </Title>
                            <Badge color='success' variant='light'>
                                Ready
                            </Badge>
                        </Group>
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <Stat label='Model type'>
                                <Text fw={500}>
                                    {life.modeling.type === 'lookalike'
                                        ? 'Expansion (lookalike)'
                                        : 'Propensity'}
                                </Text>
                            </Stat>
                            <Stat label='Priority'>
                                <Text fw={500}>
                                    {MODELING_PRIORITY_LABEL[
                                        life.modeling.priority ?? 'balanced'
                                    ] ?? 'Balanced'}
                                </Text>
                            </Stat>
                            {life.modeling.type === 'lookalike' &&
                                life.modeling.expansionDatasetId && (
                                    <Stat label='Modeled into'>
                                        <Text fw={500}>
                                            {names[
                                                life.modeling.expansionDatasetId
                                            ] ??
                                                life.modeling
                                                    .expansionDatasetId}
                                        </Text>
                                    </Stat>
                                )}
                            <Stat label='Similarity'>
                                <Text fw={500}>
                                    {life.modeling.similarityPct ?? '—'}%
                                </Text>
                            </Stat>
                            <Stat label='Seed'>
                                <Text fw={500}>
                                    {formatCompactNumber(
                                        life.modeling.seedSize
                                    )}
                                </Text>
                            </Stat>
                            <Stat label='Expanded reach'>
                                <Text fw={500}>
                                    {formatCompactNumber(
                                        life.modeling.expandedSize ?? 0
                                    )}
                                </Text>
                            </Stat>
                            <Stat label='Seed members'>
                                <Text fw={500}>
                                    {life.modeling.excludeSeed
                                        ? 'Excluded'
                                        : 'Included'}
                                </Text>
                            </Stat>
                        </SimpleGrid>
                        <Group justify='flex-end'>
                            <Button
                                variant='default'
                                onClick={() => setRemodel(true)}
                            >
                                Adjust modeling
                            </Button>
                        </Group>
                    </Stack>
                ) : (
                    <ModelingOptions
                        seedAudience={config}
                        audienceSize={config.audience_size ?? null}
                        datasets={datasets}
                        names={names}
                        onCancel={onClose}
                        onCreateModel={handleCreateModel}
                    />
                )}
            </Tabs.Panel>

            {/* --- Distribute --- */}
            <Tabs.Panel value='distribute'>
                <Stack gap='md'>
                    {(() => {
                        // Setup nudges link to the config modal that fixes them;
                        // run-level steps (e.g. "Retry the push") stay as text.
                        const needsSetup = life.deliveries.filter(
                            (d) => d.configState === 'needs-config'
                        )
                        const runSteps = life.deliveries.flatMap((d) =>
                            d.latestRun?.nextStep ? [d.latestRun.nextStep] : []
                        )
                        if (needsSetup.length === 0 && runSteps.length === 0) {
                            return null
                        }
                        return (
                            <AttentionBanner title='Next steps'>
                                <Stack gap={4} align='flex-start'>
                                    {needsSetup.map((d) => {
                                        const platform =
                                            destinations.find(
                                                (x) =>
                                                    x.id ===
                                                    d.distribution.destinationId
                                            )?.platform ??
                                            d.distribution.destinationId
                                        return (
                                            <Anchor
                                                component='button'
                                                type='button'
                                                size='sm'
                                                key={d.distribution.id}
                                                onClick={() =>
                                                    setEditing(d.distribution)
                                                }
                                            >
                                                Finish setup for {platform}
                                            </Anchor>
                                        )
                                    })}
                                    {runSteps.map((step) => (
                                        <Text size='sm' key={step}>
                                            {step}
                                        </Text>
                                    ))}
                                </Stack>
                            </AttentionBanner>
                        )
                    })()}

                    {life.deliveries.length === 0 ? (
                        <Stack gap='md' align='flex-start'>
                            <Text size='sm' c='dimmed'>
                                This segment hasn&apos;t been distributed yet.
                            </Text>
                            <Button
                                leftSection={<IconSend size={16} />}
                                onClick={() => setDistributeOpen(true)}
                            >
                                Distribute this segment
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            <Stack gap='xs'>
                                {life.deliveries.map((delivery) => {
                                    const { distribution, latestRun, runs } =
                                        delivery
                                    const dest = destinations.find(
                                        (x) =>
                                            x.id === distribution.destinationId
                                    )
                                    const badge =
                                        delivery.configState === 'needs-config'
                                            ? {
                                                  label: 'Needs setup',
                                                  color: 'warning',
                                              }
                                            : delivery.configState === 'paused'
                                              ? {
                                                    label: 'Paused',
                                                    color: 'gray',
                                                }
                                              : latestRun
                                                ? RUN_STATUS_META[
                                                      latestRun.status
                                                  ]
                                                : {
                                                      label: 'Scheduled',
                                                      color: 'gray',
                                                  }
                                    const expanded =
                                        openRuns === distribution.id
                                    const route = routeLabel(
                                        distribution,
                                        dest?.platform ??
                                            distribution.destinationId
                                    )
                                    return (
                                        <Paper
                                            key={distribution.id}
                                            withBorder
                                            p='sm'
                                            radius='md'
                                            onClick={() =>
                                                setEditing(distribution)
                                            }
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Group
                                                justify='space-between'
                                                wrap='nowrap'
                                                gap='sm'
                                            >
                                                {/* Identity + config, one line */}
                                                <Group
                                                    gap='xs'
                                                    wrap='nowrap'
                                                    style={{ minWidth: 0 }}
                                                >
                                                    <Text
                                                        size='sm'
                                                        fw={500}
                                                        truncate
                                                    >
                                                        {dest?.platform ??
                                                            distribution.destinationId}
                                                    </Text>
                                                    <Badge
                                                        size='xs'
                                                        variant='light'
                                                        color='gray'
                                                    >
                                                        {
                                                            SCHEDULE_LABEL[
                                                                distribution
                                                                    .schedule
                                                            ]
                                                        }
                                                    </Badge>
                                                    {route && (
                                                        <Badge
                                                            size='xs'
                                                            variant='light'
                                                            color='gray'
                                                        >
                                                            {route}
                                                        </Badge>
                                                    )}
                                                </Group>
                                                {/* Status + actions */}
                                                <Group gap={4} wrap='nowrap'>
                                                    <Badge
                                                        variant='light'
                                                        color={badge.color}
                                                    >
                                                        {badge.label}
                                                    </Badge>
                                                    {runs.length > 0 && (
                                                        <Tooltip
                                                            label={
                                                                expanded
                                                                    ? 'Hide runs'
                                                                    : `View runs (${runs.length})`
                                                            }
                                                        >
                                                            <ActionIcon
                                                                variant='subtle'
                                                                color='gray'
                                                                size='sm'
                                                                aria-label={
                                                                    expanded
                                                                        ? 'Hide runs'
                                                                        : 'View runs'
                                                                }
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation()
                                                                    setOpenRuns(
                                                                        expanded
                                                                            ? null
                                                                            : distribution.id
                                                                    )
                                                                }}
                                                            >
                                                                {expanded ? (
                                                                    <IconChevronUp
                                                                        size={
                                                                            16
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <IconChevronDown
                                                                        size={
                                                                            16
                                                                        }
                                                                    />
                                                                )}
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip label='Configure'>
                                                        <ActionIcon
                                                            variant='subtle'
                                                            color='gray'
                                                            size='sm'
                                                            aria-label='Configure'
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setEditing(
                                                                    distribution
                                                                )
                                                            }}
                                                        >
                                                            <IconSettings
                                                                size={16}
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                    <Tooltip
                                                        label={
                                                            distribution.paused
                                                                ? 'Resume'
                                                                : 'Pause'
                                                        }
                                                    >
                                                        <ActionIcon
                                                            variant='subtle'
                                                            color='gray'
                                                            size='sm'
                                                            aria-label={
                                                                distribution.paused
                                                                    ? 'Resume'
                                                                    : 'Pause'
                                                            }
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setDistribution(
                                                                    distribution.id,
                                                                    {
                                                                        paused: !distribution.paused,
                                                                    }
                                                                )
                                                            }}
                                                        >
                                                            {distribution.paused ? (
                                                                <IconPlayerPlay
                                                                    size={16}
                                                                />
                                                            ) : (
                                                                <IconPlayerPause
                                                                    size={16}
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Tooltip>
                                                    <DeleteButton
                                                        size='sm'
                                                        title='Remove delivery'
                                                        confirmTitle='Remove delivery'
                                                        confirmMessage={`Remove the ${
                                                            dest?.platform ??
                                                            distribution.destinationId
                                                        } delivery and its run history? This can't be undone.`}
                                                        onDelete={() =>
                                                            removeDistribution(
                                                                distribution.id
                                                            )
                                                        }
                                                    />
                                                </Group>
                                            </Group>
                                            {expanded && runs.length > 0 && (
                                                <Stack
                                                    gap={4}
                                                    mt='sm'
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Divider />
                                                    {runs.map((run) => (
                                                        <Group
                                                            key={run.id}
                                                            justify='space-between'
                                                            wrap='nowrap'
                                                        >
                                                            <RunSummary
                                                                run={run}
                                                            />
                                                            <Badge
                                                                size='xs'
                                                                variant='light'
                                                                color={
                                                                    RUN_STATUS_META[
                                                                        run
                                                                            .status
                                                                    ].color
                                                                }
                                                            >
                                                                {
                                                                    RUN_STATUS_META[
                                                                        run
                                                                            .status
                                                                    ].label
                                                                }
                                                            </Badge>
                                                        </Group>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Paper>
                                    )
                                })}
                            </Stack>
                            <Group justify='flex-end'>
                                <Button
                                    variant='light'
                                    leftSection={<IconSend size={16} />}
                                    onClick={() => setDistributeOpen(true)}
                                >
                                    Distribute to more
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            </Tabs.Panel>

            {distributeOpen && (
                <DistributeModal
                    segments={[config]}
                    threadId={threadId}
                    title='Distribute segment'
                    opened
                    onClose={() => setDistributeOpen(false)}
                />
            )}

            {editing && (
                <DistributionConfigModal
                    distribution={editing}
                    destination={destinations.find(
                        (d) => d.id === editing.destinationId
                    )}
                    opened
                    onClose={() => setEditing(null)}
                />
            )}
        </Tabs>
    )
}
